import { memberCalls2Url } from '@helpers'
import { SEATING_ZONE } from '@/core/constants/seat'

const PUBLIC_KEY_BASE64 =
    'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA0RLGK+0DlggSr22Vsh/4xpUAbAchKLbxtuh+HXYoaI7r+ucfYrjehPehBB13Pw4tPxyFQE1G8rIKfuAMqzCEJ7Njg9DoRdDhsuHgKyw7SAjRZJw4LseSleO+WUnz8XC1E647eutCqeXSiNLYuxzEOYbrUZ2SrAO+jQ6zf0xf6uG0YGjgQ7h8Gv56bz1c6ciDz2Qvw7oJ4g/Q0I19w0+KoJ9C6aMQeE1UMmx+9CsTRKqlI8eo47lkEOECLtoHsi8ohnmWvcsKRLhdHva+LB0aKW0jNclllHFxTarWrIxTbHlC3PVP6viONGVocKby6+dRz6ewmbq/gRF+tKLoMmXOQwIDAQAB'

export interface PaymentData {
    PAYMENT_URL: string
    AMOUNT: string
    MERCHANT_ACC_NO: string
    TRANSACTION_TYPE: string | number
    MERCHANT_TRANID: string
    CARD_TYPE: string
    RESPONSE_TYPE: string
    TXN_DESC: string
    TXN_SIGNATURE: string
    SIGNATURE_METHOD: string
    RETURN_URL: string
    CARD_ISSUER_BANK_COUNTRY_CODE: string
    // Card info from backend (masked/prepared)
    CARD_HOLDER_NAME: string
    CARD_NO: string
    CARD_EXP_MM: string
    CARD_EXP_YY: string
    CARD_CVC: string
    CUSTOMER_ID: string
}

export interface PaymentResponse {
    success: boolean
    reference_no: string
    payment_data: PaymentData
    errors?: string[]
}

export interface CreditCardInfo {
    cardNumber: string
    expMonth: string
    expYear: string
    cvv: string
    cardholderName: string
}

export interface CustomerInfo {
    distId: string
    distName: string
    distStatus: string
    distEmail: string
    distPhone: string
    joinDate?: string
    country: string
}

export interface TicketInfo {
    itemCode: string
    qty: number
}

export interface PaymentOptions {
    /** Force payment success for testing (dev only) */
    isForcePaymentsSuccess?: boolean
}

/**
 * Convert SEATING_ZONE display value back to key for API
 * e.g., 'Singapore & Malaysia & Brunei' → 'singaporemalaysiabrunei'
 */
function getZoneKey(zoneValue: string): string {
    const entry = Object.entries(SEATING_ZONE).find(([, value]) => value === zoneValue)
    return entry ? entry[0] : zoneValue
}

/**
 * Convert SEATING_ZONE key to display value
 * e.g., 'singaporemalaysiabrunei' → 'Singapore & Malaysia & Brunei'
 */
export function getZoneValue(zoneKey: string): string {
    return SEATING_ZONE[zoneKey as keyof typeof SEATING_ZONE] ?? zoneKey
}

/**
 * Encrypt credit card data using RSA-OAEP
 */
async function encryptCreditCard(text: string, publicKeyBase64: string): Promise<string> {
    // Decode base64 public key (DER format)
    const binaryDer = Uint8Array.from(atob(publicKeyBase64), c => c.charCodeAt(0))

    // Import the public key
    const publicKey = await window.crypto.subtle.importKey(
        'spki',
        binaryDer.buffer,
        {
            name: 'RSA-OAEP',
            hash: 'SHA-256'
        },
        false,
        ['encrypt']
    )

    // Encrypt the data
    const encrypted = await window.crypto.subtle.encrypt(
        {
            name: 'RSA-OAEP'
        },
        publicKey,
        new TextEncoder().encode(text)
    )

    // Convert encrypted data to base64
    const encryptedBase64 = btoa(String.fromCharCode(...new Uint8Array(encrypted)))

    return encryptedBase64
}

/**
 * Process payment through OCBC gateway
 * API: POST /glic-events/2027/ticket
 *
 * Returns PaymentResponse with:
 * - success: boolean
 * - reference_no: string (used for redirect to success page)
 * - payment_data: PaymentData (for OCBC form submission if needed)
 */
export async function processPayment(
    creditCard: CreditCardInfo,
    customer: CustomerInfo,
    tickets: TicketInfo[],
    zone: string,
    ticketType: string = 'earlybird',
    options: PaymentOptions = {}
): Promise<PaymentResponse> {
    const currentUrl = window.location.origin

    // Encrypt credit card data: cardNumber|YY-MM|CVV
    const encryptedCardData = await encryptCreditCard(
        `${creditCard.cardNumber}|${creditCard.expYear}-${creditCard.expMonth}|${creditCard.cvv}`,
        PUBLIC_KEY_BASE64
    )

    const isLocalhost = window.location.hostname === 'localhost'

    const data = {
        dist_id: customer.distId,
        dist_name: customer.distName,
        dist_status: customer.distStatus,
        dist_email: customer.distEmail,
        dist_phone: customer.distPhone,
        promo_code: '',
        ticket_type: ticketType,
        country: customer.country,
        zone: getZoneValue(zone),
        lang: 'en',
        join_date: customer.joinDate || new Date().toISOString(),
        items: tickets.map(t => ({
            item_code: t.itemCode,
            qty: t.qty
        })),
        agent: 'GLIC2027',
        medium: 'Internet',
        platform: navigator.platform || 'Web',
        gateway_success_url: `${currentUrl}/success`,
        gateway_error_url: `${currentUrl}/failed`,
        payments: {
            payer: customer.distName,
            ccEncrypted: encryptedCardData
        }
    }

    // Build URL with optional query params
    let path = '/glic-events/2027/ticket'
    if (options.isForcePaymentsSuccess) {
        path += '?isForcePaymentsSuccess=1'
    }

    const url = memberCalls2Url({ path })

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    })

    const result = await response.json().catch(() => ({}))

    // Extract error messages if present
    const errors: string[] = []
    if (!result.success) {
        // Prefer the human-readable gateway/hydra message, e.g.
        // "Order can't be immediately processed. Please retry. Error #P7Y4P"
        const bodyErrorMessage = result?.error?.bodyResponse?.error?.error_message
        if (bodyErrorMessage) {
            errors.push(bodyErrorMessage)
        } else if (result.message) {
            // USHOP-2914: the early-bird payment guard rejects with { message: 'glic_earlybird_ticket_already_purchased' }
            errors.push(result.message)
        }
        if (result.message_v2) {
            errors.push(...(Array.isArray(result.message_v2) ? result.message_v2 : [result.message_v2]))
        }
    }

    // Only throw for transport-level failures with no structured message to show
    if (!response.ok && errors.length === 0) {
        throw new Error(`Payment failed: ${response.status} ${response.statusText}`)
    }

    return {
        success: result.success ?? false,
        reference_no: result.reference_no ?? '',
        payment_data: result.payment_data as PaymentData,
        errors: errors.length > 0 ? errors : undefined
    }
}

/**
 * Submit payment form to OCBC gateway
 */
export function submitPaymentForm(formId: string): void {
    const form = document.getElementById(formId) as HTMLFormElement | null
    if (form) {
        form.submit()
    }
}
